import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { apiRequest } from '../utils/auth';
import { 
  Calculator, Save, FileDown, Plus, Trash2, GripVertical, 
  ChevronDown, ChevronUp, Settings, RefreshCw, Loader2, Copy, X
} from 'lucide-react';

// Material Types
const MATERIAL_TYPES = ['Aluminium', 'Copper', 'Stainless Steel', 'Mild Steel', 'Brass', 'Other'];
const RM_SHAPES = ['Rod', 'Pipe', 'Plate/Sheet'];
const CUTTING_TYPES = ['Bandsaw', 'Laser', 'Waterjet'];
const MACHINE_TYPES = ['Lathe', 'CNC', 'VMC', 'Drill'];
const MACHINE_SIZES = ['Small', 'Medium', 'Large'];

// Default Rates for Materials
const DEFAULT_RATES = {
  aluminium: { conversion: 65, packaging: 3.5, transport: 4, surcharge: 8.25, interest: 3, scrapPercent: 40 },
  copper: { conversion: 55, packaging: 3.5, transport: 4, surcharge: 8.25, interest: 3, scrapPercent: 70 },
  stainless_steel: { scrapPercent: 40 },
  other: { scrapRate: 25 }
};

// Machine Hourly Rates as per specification
const MACHINE_HOURLY_RATES = {
  Lathe: { Small: 240, Medium: 400, Large: 600 },
  CNC: { Small: 300, Medium: 450, Large: 600 },
  VMC: { Small: 600, Medium: 750, Large: 1000 },
  Drill: { Small: 120, Medium: 180, Large: 240 }
};

// Cutting Defaults
const CUTTING_DEFAULTS = {
  bandsaw_rate: 1.5,  // Rs per sq inch
  laser_rate: 8,      // Rs per mm per meter
  waterjet_rate: 15   // Rs per mm per meter
};

// Operations List with specific logic flags
const OPERATIONS_LIST = [
  { id: 'cutting', name: 'Cutting', hasSubtype: true, unit: 'Rs' },
  { id: 'forging', name: 'Forging', defaultRate: 20, unit: 'Rs/kg', usesGrossWeight: true },
  { id: 'normalizing', name: 'Normalizing', defaultRate: 10, unit: 'Rs/kg', usesForgeWeight: true },
  { id: 'hardening', name: 'Hardening & Tempering', defaultRate: 20, unit: 'Rs/kg', usesForgeWeight: true },
  { id: 'grinding', name: 'Grinding', defaultRate: 15, unit: 'Rs/kg' },
  { id: 'painting', name: 'Painting', defaultRate: 8, unit: 'Rs/piece' },
  { id: 'silver_plating', name: 'Silver Plating', defaultRate: 50, unit: 'Rs/piece' },
  { id: 'nickel_plating', name: 'Nickel Plating', defaultRate: 25, unit: 'Rs/piece' },
  { id: 'phosphating', name: 'Phosphating', defaultRate: 12, unit: 'Rs/piece' },
  { id: 'anodizing', name: 'Anodizing', defaultRate: 30, unit: 'Rs/piece' },
  { id: 'zinc_plating', name: 'Zinc Plating', defaultRate: 18, unit: 'Rs/piece' },
  { id: 'nitriding', name: 'Nitriding', defaultRate: 35, unit: 'Rs/kg' },
  { id: 'buffing', name: 'Buffing', defaultRate: 5, unit: 'Rs/piece' },
  { id: 'roughing', name: 'Roughing', defaultRate: 10, unit: 'Rs/piece' },
  { id: 'drilling_op', name: 'Drilling', defaultRate: 8, unit: 'Rs/piece' },
  { id: 'welding', name: 'Welding', defaultRate: 15, unit: 'Rs/piece' },
  { id: 'machining', name: 'Machining', hasMachine: true }
];

// Material Densities (g/cm³)
const MATERIAL_DENSITIES = {
  'Aluminium': 2.7,
  'Copper': 8.96,
  'Stainless Steel': 8.0,
  'Mild Steel': 7.85,
  'Brass': 8.5,
  'Other': 7.85
};

export default function QuotationCosting() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    header: true, rawMaterial: true, operations: true, machining: false, summary: true
  });

  // Quotation Header
  const [header, setHeader] = useState({
    customer_name: '',
    part_number: '',
    part_name: '',
    drawing_number: '',
    material_grade: '',
    quantity: 1,
    revision: '1',
    date: new Date().toISOString().split('T')[0],
    prepared_by: '',
    notes: ''
  });

  // Raw Material Section
  const [rawMaterial, setRawMaterial] = useState({
    material_type: 'Aluminium',
    rm_shape: 'Rod',
    // Rod/Pipe dimensions
    od: '',
    id: '',
    length: '',
    // Plate dimensions
    width: '',
    breadth: '',
    // Common
    density: MATERIAL_DENSITIES['Aluminium'],
    finish_weight: '',
    recovery_percent: 90,
    // Pricing
    base_rate: '',
    nalco_rate: '',
    bme_rate: '',
    manual_rm_rate: '',
    conversion: DEFAULT_RATES.aluminium.conversion,
    packaging: DEFAULT_RATES.aluminium.packaging,
    transport: DEFAULT_RATES.aluminium.transport,
    surcharge: DEFAULT_RATES.aluminium.surcharge,
    discount_percent: 0,
    interest_percent: DEFAULT_RATES.aluminium.interest,
    // Scrap
    scrap_rate_override: '',
    // Calculated (will be computed)
    gross_weight: 0,
    scrap_weight: 0,
    final_rm_rate: 0,
    gross_rm_value: 0,
    scrap_value: 0,
    net_rm_cost: 0
  });

  // Operations
  const [selectedOperations, setSelectedOperations] = useState([]);

  // Machining
  const [machiningSteps, setMachiningSteps] = useState([]);

  // Summary
  const [summary, setSummary] = useState({
    net_rm_cost: 0,
    total_operation_cost: 0,
    total_machining_cost: 0,
    total_manufacturing_cost: 0,
    profit_margin_percent: 15,
    profit_amount: 0,
    final_quotation_value: 0
  });

  // Masters Modal State
  const [showMastersModal, setShowMastersModal] = useState(false);
  const [mastersTab, setMastersTab] = useState('materials');
  const [mastersMaterials, setMastersMaterials] = useState([]);
  const [mastersOperations, setMastersOperations] = useState([]);
  const [mastersMachines, setMastersMachines] = useState([]);
  const [savingMasters, setSavingMasters] = useState(false);

  // Fetch customers on mount
  useEffect(() => {
    fetchCustomers();
    fetchMasters();
    if (isEditing) {
      fetchQuotation(id);
    }
  }, [id]);

  const fetchMasters = async () => {
    try {
      const [materialsRes, operationsRes, machinesRes] = await Promise.all([
        apiRequest('/api/masters/raw-materials'),
        apiRequest('/api/masters/operations'),
        apiRequest('/api/masters/machines')
      ]);
      setMastersMaterials(materialsRes.materials || []);
      setMastersOperations(operationsRes.operations || []);
      setMastersMachines(machinesRes.machines || []);
    } catch (error) {
      console.error('Failed to fetch masters:', error);
    }
  };

  const saveMasters = async () => {
    setSavingMasters(true);
    try {
      await Promise.all([
        apiRequest('/api/masters/raw-materials', { method: 'PUT', body: JSON.stringify(mastersMaterials) }),
        apiRequest('/api/masters/operations', { method: 'PUT', body: JSON.stringify(mastersOperations) }),
        apiRequest('/api/masters/machines', { method: 'PUT', body: JSON.stringify(mastersMachines) })
      ]);
      toast.success('Masters saved successfully');
      setShowMastersModal(false);
    } catch (error) {
      toast.error('Failed to save masters');
    } finally {
      setSavingMasters(false);
    }
  };

  const addMasterMaterial = () => {
    setMastersMaterials([...mastersMaterials, {
      material_type: 'New Material',
      manual_rate: 0,
      conversion: 0,
      packaging: 0,
      transport: 0,
      surcharge: 0,
      interest_percent: 0,
      scrap_percent: 30,
      density: 7.85
    }]);
  };

  const addMasterOperation = () => {
    setMastersOperations([...mastersOperations, {
      operation_name: 'New Operation',
      default_rate: 10,
      unit: 'Rs/piece'
    }]);
  };

  const addMasterMachine = () => {
    setMastersMachines([...mastersMachines, {
      machine_type: 'New Machine',
      size: 'Medium',
      hourly_rate: 300
    }]);
  };

  const fetchCustomers = async () => {
    try {
      const data = await apiRequest('/api/customers');
      setCustomers(data.customers || []);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchQuotation = async (quotationId) => {
    setLoading(true);
    try {
      const data = await apiRequest(`/api/quotation-costing/${quotationId}`);
      if (data) {
        setHeader(data.header || header);
        setRawMaterial(data.raw_material || rawMaterial);
        setSelectedOperations(data.operations || []);
        setMachiningSteps(data.machining_steps || []);
        setSummary(data.summary || summary);
      }
    } catch (error) {
      toast.error('Failed to load quotation');
    } finally {
      setLoading(false);
    }
  };

  // Calculate Gross Weight based on shape
  const calculateGrossWeight = useCallback(() => {
    const { rm_shape, od, id: innerId, length, width, breadth, density } = rawMaterial;
    let grossWeight = 0;
    const PI = 3.14159;
    const d = parseFloat(density) || 0;

    if (rm_shape === 'Rod') {
      const outerDia = parseFloat(od) || 0;
      const len = parseFloat(length) || 0;
      // Formula: (π × OD² × Length × Density) / 4000000
      grossWeight = (PI * Math.pow(outerDia, 2) * len * d) / 4000000;
    } else if (rm_shape === 'Pipe') {
      const outerDia = parseFloat(od) || 0;
      const innerDia = parseFloat(innerId) || 0;
      const len = parseFloat(length) || 0;
      // Formula: (π × (OD² - ID²) × Length × Density) / 4000000
      grossWeight = (PI * (Math.pow(outerDia, 2) - Math.pow(innerDia, 2)) * len * d) / 4000000;
    } else if (rm_shape === 'Plate/Sheet') {
      const w = parseFloat(width) || 0;
      const b = parseFloat(breadth) || 0;
      const len = parseFloat(length) || 0;
      // Formula: (Width × Breadth × Length × Density) / 1000000
      grossWeight = (w * b * len * d) / 1000000;
    }

    return Math.round(grossWeight * 1000) / 1000; // Round to 3 decimals
  }, [rawMaterial]);

  // Calculate Final RM Rate
  const calculateFinalRMRate = useCallback(() => {
    const { material_type, nalco_rate, bme_rate, manual_rm_rate, conversion, packaging, transport, surcharge, discount_percent, interest_percent } = rawMaterial;
    
    let baseRate = 0;
    if (material_type === 'Aluminium') {
      baseRate = parseFloat(nalco_rate) || 0;
    } else if (material_type === 'Copper') {
      baseRate = parseFloat(bme_rate) || 0;
    } else {
      baseRate = parseFloat(manual_rm_rate) || 0;
    }

    const conv = parseFloat(conversion) || 0;
    const pack = parseFloat(packaging) || 0;
    const trans = parseFloat(transport) || 0;
    const sur = parseFloat(surcharge) || 0;
    const disc = parseFloat(discount_percent) || 0;
    const interest = parseFloat(interest_percent) || 0;

    // Calculate subtotal before discount and interest
    let subtotal = baseRate + conv + pack + trans + sur;
    
    // Apply discount
    subtotal = subtotal * (1 - disc / 100);
    
    // Apply interest
    subtotal = subtotal * (1 + interest / 100);

    return Math.round(subtotal * 100) / 100;
  }, [rawMaterial]);

  // Calculate Scrap Rate
  const calculateScrapRate = useCallback(() => {
    const { material_type, nalco_rate, bme_rate, manual_rm_rate, scrap_rate_override } = rawMaterial;
    
    if (scrap_rate_override && parseFloat(scrap_rate_override) > 0) {
      return parseFloat(scrap_rate_override);
    }

    if (material_type === 'Aluminium') {
      return (parseFloat(nalco_rate) || 0) * 0.4; // 40% of NALCO
    } else if (material_type === 'Copper') {
      return (parseFloat(bme_rate) || 0) * 0.7; // 70% of BME
    } else if (material_type === 'Stainless Steel') {
      return (parseFloat(manual_rm_rate) || 0) * 0.4; // 40% of RM Rate
    } else {
      return 25; // Default 25 Rs/kg
    }
  }, [rawMaterial]);

  // Calculate all RM costs
  const calculateRMCosts = useCallback(() => {
    const grossWeight = calculateGrossWeight();
    const finishWeight = parseFloat(rawMaterial.finish_weight) || 0;
    const recoveryPercent = parseFloat(rawMaterial.recovery_percent) || 0;
    const finalRMRate = calculateFinalRMRate();
    const scrapRate = calculateScrapRate();

    // Scrap Weight = (Gross Weight - Finish Weight) × Recovery %
    const scrapWeight = (grossWeight - finishWeight) * (recoveryPercent / 100);
    
    // Gross RM Value = Gross Weight × RM Rate
    const grossRMValue = grossWeight * finalRMRate;
    
    // Scrap Value = Scrap Weight × Scrap Rate
    const scrapValue = scrapWeight * scrapRate;
    
    // Net RM Cost = Gross RM Value - Scrap Value
    const netRMCost = grossRMValue - scrapValue;

    return {
      gross_weight: Math.round(grossWeight * 1000) / 1000,
      scrap_weight: Math.round(scrapWeight * 1000) / 1000,
      final_rm_rate: finalRMRate,
      scrap_rate: Math.round(scrapRate * 100) / 100,
      gross_rm_value: Math.round(grossRMValue * 100) / 100,
      scrap_value: Math.round(scrapValue * 100) / 100,
      net_rm_cost: Math.round(netRMCost * 100) / 100
    };
  }, [rawMaterial, calculateGrossWeight, calculateFinalRMRate, calculateScrapRate]);

  // Calculate Operation Costs with specific logic per operation type
  const calculateOperationCosts = useCallback(() => {
    const rmCosts = calculateRMCosts();
    let totalOpCost = 0;
    
    // Find forging weight from operations (needed for normalizing and hardening)
    const forgingOp = selectedOperations.find(o => o.id === 'forging');
    const forgingWeight = parseFloat(forgingOp?.forging_weight) || 0;

    const updatedOps = selectedOperations.map(op => {
      let cost = 0;
      let calculatedArea = 0;

      if (op.id === 'cutting') {
        const defaultRate = op.cutting_type === 'Bandsaw' ? CUTTING_DEFAULTS.bandsaw_rate : 
                           op.cutting_type === 'Laser' ? CUTTING_DEFAULTS.laser_rate : 
                           CUTTING_DEFAULTS.waterjet_rate;
        const rate = parseFloat(op.rate) || defaultRate;
        
        if (op.cutting_type === 'Bandsaw') {
          // Bandsaw: Area in sq inch × Rate
          // For Rod/Pipe: 0.0013 * OD * OD
          // For Sheet/Strip: 0.0155 * W * B
          if (rawMaterial.rm_shape === 'Rod' || rawMaterial.rm_shape === 'Pipe') {
            calculatedArea = 0.0013 * Math.pow(parseFloat(rawMaterial.od) || 0, 2);
          } else {
            calculatedArea = 0.0155 * (parseFloat(rawMaterial.width) || 0) * (parseFloat(rawMaterial.breadth) || 0);
          }
          const area = parseFloat(op.area_override) || calculatedArea;
          cost = rate * area;
        } else if (op.cutting_type === 'Laser' || op.cutting_type === 'Waterjet') {
          // Laser/Waterjet: Rate per mm per meter × Area in mm per meter (manual entry)
          const laserArea = parseFloat(op.laser_area) || 0;
          cost = rate * laserArea;
        }
      } else if (op.id === 'forging') {
        // Forging: cost per kg * forging weight
        // Forging input weight = RM gross weight (displayed)
        // Forging weight = manual entry
        const rate = parseFloat(op.rate) || 20;
        const forgeWeight = parseFloat(op.forging_weight) || 0;
        cost = rate * forgeWeight;
      } else if (op.id === 'normalizing') {
        // Normalizing: rate per kg * weight (weight = forging weight)
        const rate = parseFloat(op.rate) || 10;
        const weight = parseFloat(op.weight_override) || forgingWeight;
        cost = rate * weight;
      } else if (op.id === 'hardening') {
        // Hardening & Tempering: rate per kg * weight (weight = forging weight, editable)
        const rate = parseFloat(op.rate) || 20;
        const weight = parseFloat(op.weight_override) || forgingWeight;
        cost = rate * weight;
      } else {
        // Standard operations (rate × weight or rate per piece)
        const rate = parseFloat(op.rate) || parseFloat(op.defaultRate) || 0;
        if (op.unit === 'Rs/kg') {
          cost = rate * rmCosts.gross_weight;
        } else {
          cost = rate; // Per piece
        }
      }

      // Allow manual cost override
      if (op.manual_cost !== undefined && op.manual_cost !== '') {
        cost = parseFloat(op.manual_cost);
      }

      totalOpCost += cost;
      return { 
        ...op, 
        calculated_cost: Math.round(cost * 100) / 100,
        calculated_area: Math.round(calculatedArea * 1000) / 1000,
        input_weight: rmCosts.gross_weight  // For forging display
      };
    });

    return { operations: updatedOps, total: Math.round(totalOpCost * 100) / 100 };
  }, [selectedOperations, rawMaterial, calculateRMCosts]);

  // Calculate Machining Costs with correct formula
  const calculateMachiningCosts = useCallback(() => {
    let totalMachiningCost = 0;

    const updatedSteps = machiningSteps.map(step => {
      const hourlyRate = MACHINE_HOURLY_RATES[step.machine_type]?.[step.machine_size] || 0;
      const cycleTime = parseFloat(step.cycle_time) || 1;
      const efficiency = parseFloat(step.efficiency) || 80;

      // OPH (Output Per Hour) = (3600 / Cycle Time in seconds) × Efficiency%
      const oph = (3600 / cycleTime) * (efficiency / 100);
      
      // Cost Per Piece = Machine Hour Rate / OPH
      let costPerPiece = oph > 0 ? hourlyRate / oph : 0;

      // Allow manual override
      if (step.manual_cost !== undefined && step.manual_cost !== '') {
        costPerPiece = parseFloat(step.manual_cost);
      }

      totalMachiningCost += costPerPiece;

      return {
        ...step,
        hourly_rate: hourlyRate,
        oph: Math.round(oph * 100) / 100,
        calculated_cost: Math.round(costPerPiece * 100) / 100
      };
    });

    return { steps: updatedSteps, total: Math.round(totalMachiningCost * 100) / 100 };
  }, [machiningSteps]);

  // Trigger for operation cost recalculation
  const [opCalcTrigger, setOpCalcTrigger] = useState(0);
  const [machCalcTrigger, setMachCalcTrigger] = useState(0);

  // Recalculate RM costs when inputs change
  useEffect(() => {
    const rmCosts = calculateRMCosts();

    // Update raw material state with calculated values
    setRawMaterial(prev => ({
      ...prev,
      gross_weight: rmCosts.gross_weight,
      scrap_weight: rmCosts.scrap_weight,
      final_rm_rate: rmCosts.final_rm_rate,
      scrap_rate: rmCosts.scrap_rate,
      gross_rm_value: rmCosts.gross_rm_value,
      scrap_value: rmCosts.scrap_value,
      net_rm_cost: rmCosts.net_rm_cost
    }));
  }, [rawMaterial.od, rawMaterial.id, rawMaterial.length, rawMaterial.width, rawMaterial.breadth, 
      rawMaterial.density, rawMaterial.finish_weight, rawMaterial.recovery_percent,
      rawMaterial.nalco_rate, rawMaterial.bme_rate, rawMaterial.manual_rm_rate,
      rawMaterial.conversion, rawMaterial.packaging, rawMaterial.transport,
      rawMaterial.surcharge, rawMaterial.discount_percent, rawMaterial.interest_percent,
      rawMaterial.scrap_rate_override, rawMaterial.material_type, rawMaterial.rm_shape]);

  // Recalculate operation costs when operations or trigger changes
  useEffect(() => {
    if (selectedOperations.length > 0) {
      const opCosts = calculateOperationCosts();
      // Only update if costs have changed to avoid infinite loop
      const hasChanges = opCosts.operations.some((op, idx) => 
        op.calculated_cost !== selectedOperations[idx]?.calculated_cost ||
        op.calculated_area !== selectedOperations[idx]?.calculated_area
      );
      if (hasChanges) {
        setSelectedOperations(opCosts.operations);
      }
    }
  }, [opCalcTrigger, rawMaterial.gross_weight, rawMaterial.od, rawMaterial.width, rawMaterial.breadth, rawMaterial.rm_shape]);

  // Recalculate machining costs when steps or trigger changes
  useEffect(() => {
    if (machiningSteps.length > 0) {
      const machCosts = calculateMachiningCosts();
      const hasChanges = machCosts.steps.some((step, idx) => 
        step.calculated_cost !== machiningSteps[idx]?.calculated_cost ||
        step.oph !== machiningSteps[idx]?.oph
      );
      if (hasChanges) {
        setMachiningSteps(machCosts.steps);
      }
    }
  }, [machCalcTrigger]);

  // Calculate summary when costs change
  useEffect(() => {
    const rmCost = parseFloat(rawMaterial.net_rm_cost) || 0;
    const opTotal = selectedOperations.reduce((sum, op) => sum + (parseFloat(op.calculated_cost) || 0), 0);
    const machTotal = machiningSteps.reduce((sum, step) => sum + (parseFloat(step.calculated_cost) || 0), 0);
    
    const totalManufacturingCost = rmCost + opTotal + machTotal;
    const profitPercent = parseFloat(summary.profit_margin_percent) || 0;
    const profitAmount = totalManufacturingCost * (profitPercent / 100);
    const finalValue = totalManufacturingCost + profitAmount;

    setSummary(prev => ({
      ...prev,
      net_rm_cost: rmCost,
      total_operation_cost: Math.round(opTotal * 100) / 100,
      total_machining_cost: Math.round(machTotal * 100) / 100,
      total_manufacturing_cost: Math.round(totalManufacturingCost * 100) / 100,
      profit_amount: Math.round(profitAmount * 100) / 100,
      final_quotation_value: Math.round(finalValue * 100) / 100
    }));
  }, [rawMaterial.net_rm_cost, selectedOperations, machiningSteps, summary.profit_margin_percent]);

  // Handle material type change
  const handleMaterialTypeChange = (value) => {
    const defaults = value === 'Aluminium' ? DEFAULT_RATES.aluminium :
                     value === 'Copper' ? DEFAULT_RATES.copper : {};
    
    setRawMaterial(prev => ({
      ...prev,
      material_type: value,
      density: MATERIAL_DENSITIES[value] || 7.85,
      conversion: defaults.conversion || 0,
      packaging: defaults.packaging || 0,
      transport: defaults.transport || 0,
      surcharge: defaults.surcharge || 0,
      interest_percent: defaults.interest || 0,
      nalco_rate: value === 'Aluminium' ? prev.nalco_rate : '',
      bme_rate: value === 'Copper' ? prev.bme_rate : '',
      manual_rm_rate: !['Aluminium', 'Copper'].includes(value) ? prev.manual_rm_rate : ''
    }));
  };

  // Add operation
  const addOperation = (op) => {
    const exists = selectedOperations.find(o => o.id === op.id);
    if (!exists) {
      const newOp = { 
        ...op, 
        enabled: true,
        rate: op.defaultRate || '',
        cutting_type: op.hasSubtype ? 'Bandsaw' : undefined,
        area: '',
        manual_cost: ''
      };
      setSelectedOperations([...selectedOperations, newOp]);
      // Trigger recalculation
      setTimeout(() => setOpCalcTrigger(t => t + 1), 50);
    }
  };

  // Remove operation
  const removeOperation = (opId) => {
    setSelectedOperations(selectedOperations.filter(o => o.id !== opId));
  };

  // Update operation and trigger recalculation
  const updateOperation = (idx, updates) => {
    const updated = [...selectedOperations];
    updated[idx] = { ...updated[idx], ...updates };
    setSelectedOperations(updated);
    // Trigger recalculation after state update
    setTimeout(() => setOpCalcTrigger(t => t + 1), 50);
  };

  // Add machining step
  const addMachiningStep = () => {
    setMachiningSteps([...machiningSteps, {
      id: Date.now(),
      machine_type: 'Lathe',
      machine_size: 'Medium',
      cycle_time: 60,
      efficiency: 80,
      manual_cost: ''
    }]);
    setTimeout(() => setMachCalcTrigger(t => t + 1), 50);
  };

  // Remove machining step
  const removeMachiningStep = (stepId) => {
    setMachiningSteps(machiningSteps.filter(s => s.id !== stepId));
  };

  // Update machining step and trigger recalculation
  const updateMachiningStep = (idx, updates) => {
    const updated = [...machiningSteps];
    updated[idx] = { ...updated[idx], ...updates };
    setMachiningSteps(updated);
    setTimeout(() => setMachCalcTrigger(t => t + 1), 50);
  };

  // Toggle section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Save quotation
  const saveQuotation = async () => {
    if (!header.customer_name || !header.part_number) {
      toast.error('Please fill Customer Name and Part Number');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        header,
        raw_material: rawMaterial,
        operations: selectedOperations,
        machining_steps: machiningSteps,
        summary
      };

      if (isEditing) {
        await apiRequest(`/api/quotation-costing/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
        toast.success('Quotation updated successfully');
      } else {
        const result = await apiRequest('/api/quotation-costing', { method: 'POST', body: JSON.stringify(payload) });
        toast.success('Quotation saved successfully');
        navigate(`/quotation-costing/${result.id}`);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save quotation');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto" data-testid="quotation-costing-page">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A2540]">Quotation Costing Engine</h1>
            <p className="text-sm text-gray-500 mt-1">Calculate manufacturing cost and generate quotations</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowMastersModal(true)} data-testid="masters-btn">
              <Settings className="w-4 h-4 mr-2" />
              Masters
            </Button>
            <Button variant="outline" onClick={() => navigate('/quotation-costing-list')}>
              View All
            </Button>
            <Button onClick={saveQuotation} disabled={saving} className="bg-[#0A2540]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Quotation
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4">
            
            {/* Quotation Header */}
            <Card className="border-gray-200">
              <div 
                className="p-4 bg-gray-50 border-b cursor-pointer flex justify-between items-center"
                onClick={() => toggleSection('header')}
              >
                <h2 className="font-semibold text-[#0A2540]">Quotation Header</h2>
                {expandedSections.header ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </div>
              {expandedSections.header && (
                <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Customer Name *</Label>
                    <Select value={header.customer_name} onValueChange={(v) => setHeader({...header, customer_name: v})}>
                      <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                      <SelectContent>
                        {customers.map(c => (
                          <SelectItem key={c.customer_code} value={c.company_name}>{c.company_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Part Number *</Label>
                    <Input value={header.part_number} onChange={(e) => setHeader({...header, part_number: e.target.value})} />
                  </div>
                  <div>
                    <Label>Part Name</Label>
                    <Input value={header.part_name} onChange={(e) => setHeader({...header, part_name: e.target.value})} />
                  </div>
                  <div>
                    <Label>Drawing Number</Label>
                    <Input value={header.drawing_number} onChange={(e) => setHeader({...header, drawing_number: e.target.value})} />
                  </div>
                  <div>
                    <Label>Material Grade</Label>
                    <Input value={header.material_grade} onChange={(e) => setHeader({...header, material_grade: e.target.value})} />
                  </div>
                  <div>
                    <Label>Quantity</Label>
                    <Input type="number" value={header.quantity} onChange={(e) => setHeader({...header, quantity: parseInt(e.target.value) || 1})} />
                  </div>
                  <div>
                    <Label>Revision</Label>
                    <Input value={header.revision} onChange={(e) => setHeader({...header, revision: e.target.value})} />
                  </div>
                  <div>
                    <Label>Date</Label>
                    <Input type="date" value={header.date} onChange={(e) => setHeader({...header, date: e.target.value})} />
                  </div>
                  <div>
                    <Label>Prepared By</Label>
                    <Input value={header.prepared_by} onChange={(e) => setHeader({...header, prepared_by: e.target.value})} />
                  </div>
                  <div className="col-span-full">
                    <Label>Notes</Label>
                    <Textarea value={header.notes} onChange={(e) => setHeader({...header, notes: e.target.value})} rows={2} />
                  </div>
                </div>
              )}
            </Card>

            {/* Raw Material Section */}
            <Card className="border-gray-200">
              <div 
                className="p-4 bg-blue-50 border-b cursor-pointer flex justify-between items-center"
                onClick={() => toggleSection('rawMaterial')}
              >
                <h2 className="font-semibold text-[#0A2540]">Raw Material Costing</h2>
                {expandedSections.rawMaterial ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </div>
              {expandedSections.rawMaterial && (
                <div className="p-4 space-y-4">
                  {/* Material Selection */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label>Material Type</Label>
                      <Select value={rawMaterial.material_type} onValueChange={handleMaterialTypeChange}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {MATERIAL_TYPES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>RM Shape</Label>
                      <Select value={rawMaterial.rm_shape} onValueChange={(v) => setRawMaterial({...rawMaterial, rm_shape: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {RM_SHAPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Density (g/cm³)</Label>
                      <Input type="number" step="0.01" value={rawMaterial.density} onChange={(e) => setRawMaterial({...rawMaterial, density: e.target.value})} />
                    </div>
                    <div>
                      <Label>Recovery %</Label>
                      <Input type="number" value={rawMaterial.recovery_percent} onChange={(e) => setRawMaterial({...rawMaterial, recovery_percent: e.target.value})} />
                    </div>
                  </div>

                  {/* Dimensions based on shape */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-3">Dimensions (mm)</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {(rawMaterial.rm_shape === 'Rod' || rawMaterial.rm_shape === 'Pipe') && (
                        <>
                          <div>
                            <Label>OD (mm)</Label>
                            <Input type="number" step="0.01" value={rawMaterial.od} onChange={(e) => setRawMaterial({...rawMaterial, od: e.target.value})} placeholder="Outer Diameter" />
                          </div>
                          {rawMaterial.rm_shape === 'Pipe' && (
                            <div>
                              <Label>ID (mm)</Label>
                              <Input type="number" step="0.01" value={rawMaterial.id} onChange={(e) => setRawMaterial({...rawMaterial, id: e.target.value})} placeholder="Inner Diameter" />
                            </div>
                          )}
                          <div>
                            <Label>Length (mm)</Label>
                            <Input type="number" step="0.01" value={rawMaterial.length} onChange={(e) => setRawMaterial({...rawMaterial, length: e.target.value})} />
                          </div>
                        </>
                      )}
                      {rawMaterial.rm_shape === 'Plate/Sheet' && (
                        <>
                          <div>
                            <Label>Width (mm)</Label>
                            <Input type="number" step="0.01" value={rawMaterial.width} onChange={(e) => setRawMaterial({...rawMaterial, width: e.target.value})} />
                          </div>
                          <div>
                            <Label>Breadth (mm)</Label>
                            <Input type="number" step="0.01" value={rawMaterial.breadth} onChange={(e) => setRawMaterial({...rawMaterial, breadth: e.target.value})} />
                          </div>
                          <div>
                            <Label>Length/Thickness (mm)</Label>
                            <Input type="number" step="0.01" value={rawMaterial.length} onChange={(e) => setRawMaterial({...rawMaterial, length: e.target.value})} />
                          </div>
                        </>
                      )}
                      <div>
                        <Label>Finish Weight (kg)</Label>
                        <Input type="number" step="0.001" value={rawMaterial.finish_weight} onChange={(e) => setRawMaterial({...rawMaterial, finish_weight: e.target.value})} />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {rawMaterial.rm_shape === 'Rod' && 'Formula: Gross Weight = (π × OD² × Length × Density) / 4,000,000'}
                      {rawMaterial.rm_shape === 'Pipe' && 'Formula: Gross Weight = (π × (OD² - ID²) × Length × Density) / 4,000,000'}
                      {rawMaterial.rm_shape === 'Plate/Sheet' && 'Formula: Gross Weight = (Width × Breadth × Length × Density) / 1,000,000'}
                    </p>
                  </div>

                  {/* Pricing Section */}
                  <div className="p-3 bg-yellow-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-3">RM Pricing (Rs/kg)</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {rawMaterial.material_type === 'Aluminium' && (
                        <div>
                          <Label className="text-blue-700">NALCO Rate *</Label>
                          <Input type="number" step="0.01" value={rawMaterial.nalco_rate} onChange={(e) => setRawMaterial({...rawMaterial, nalco_rate: e.target.value})} className="border-blue-300" />
                        </div>
                      )}
                      {rawMaterial.material_type === 'Copper' && (
                        <div>
                          <Label className="text-orange-700">BME Rate *</Label>
                          <Input type="number" step="0.01" value={rawMaterial.bme_rate} onChange={(e) => setRawMaterial({...rawMaterial, bme_rate: e.target.value})} className="border-orange-300" />
                        </div>
                      )}
                      {!['Aluminium', 'Copper'].includes(rawMaterial.material_type) && (
                        <div>
                          <Label>Manual RM Rate *</Label>
                          <Input type="number" step="0.01" value={rawMaterial.manual_rm_rate} onChange={(e) => setRawMaterial({...rawMaterial, manual_rm_rate: e.target.value})} />
                        </div>
                      )}
                      <div>
                        <Label>Conversion</Label>
                        <Input type="number" step="0.01" value={rawMaterial.conversion} onChange={(e) => setRawMaterial({...rawMaterial, conversion: e.target.value})} />
                      </div>
                      <div>
                        <Label>Packaging</Label>
                        <Input type="number" step="0.01" value={rawMaterial.packaging} onChange={(e) => setRawMaterial({...rawMaterial, packaging: e.target.value})} />
                      </div>
                      <div>
                        <Label>Transport</Label>
                        <Input type="number" step="0.01" value={rawMaterial.transport} onChange={(e) => setRawMaterial({...rawMaterial, transport: e.target.value})} />
                      </div>
                      <div>
                        <Label>Surcharge</Label>
                        <Input type="number" step="0.01" value={rawMaterial.surcharge} onChange={(e) => setRawMaterial({...rawMaterial, surcharge: e.target.value})} />
                      </div>
                      <div>
                        <Label>Discount %</Label>
                        <Input type="number" step="0.1" value={rawMaterial.discount_percent} onChange={(e) => setRawMaterial({...rawMaterial, discount_percent: e.target.value})} />
                      </div>
                      <div>
                        <Label>Interest %</Label>
                        <Input type="number" step="0.1" value={rawMaterial.interest_percent} onChange={(e) => setRawMaterial({...rawMaterial, interest_percent: e.target.value})} />
                      </div>
                      <div>
                        <Label>Scrap Rate Override</Label>
                        <Input type="number" step="0.01" value={rawMaterial.scrap_rate_override} onChange={(e) => setRawMaterial({...rawMaterial, scrap_rate_override: e.target.value})} placeholder="Auto" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Scrap Rate Formula: Aluminium = 40% of NALCO | Copper = 70% of BME | SS = 40% of RM Rate | Others = ₹25/kg
                    </p>
                  </div>

                  {/* Calculated Values */}
                  <div className="grid grid-cols-4 md:grid-cols-7 gap-3 p-3 bg-green-50 rounded-lg">
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Gross Wt</p>
                      <p className="font-bold text-lg">{rawMaterial.gross_weight} kg</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Scrap Wt</p>
                      <p className="font-bold text-lg">{rawMaterial.scrap_weight} kg</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Final RM Rate</p>
                      <p className="font-bold text-lg">₹{rawMaterial.final_rm_rate}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Scrap Rate</p>
                      <p className="font-bold text-lg text-blue-600">₹{rawMaterial.scrap_rate || calculateScrapRate()}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Gross RM Value</p>
                      <p className="font-bold text-lg">₹{rawMaterial.gross_rm_value}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Scrap Value</p>
                      <p className="font-bold text-lg text-orange-600">-₹{rawMaterial.scrap_value}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Net RM Cost</p>
                      <p className="font-bold text-lg text-green-600">₹{rawMaterial.net_rm_cost}</p>
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {/* Operations Section */}
            <Card className="border-gray-200">
              <div 
                className="p-4 bg-purple-50 border-b cursor-pointer flex justify-between items-center"
                onClick={() => toggleSection('operations')}
              >
                <h2 className="font-semibold text-[#0A2540]">Operations Costing</h2>
                {expandedSections.operations ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </div>
              {expandedSections.operations && (
                <div className="p-4">
                  {/* Add Operations */}
                  <div className="flex flex-wrap gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
                    {OPERATIONS_LIST.filter(op => op.id !== 'machining').map(op => (
                      <Button
                        key={op.id}
                        variant={selectedOperations.find(o => o.id === op.id) ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => selectedOperations.find(o => o.id === op.id) ? removeOperation(op.id) : addOperation(op)}
                        className="text-xs"
                      >
                        {op.name}
                      </Button>
                    ))}
                  </div>

                  {/* Selected Operations */}
                  {selectedOperations.length > 0 && (
                    <div className="space-y-3">
                      {selectedOperations.map((op, idx) => (
                        <div key={op.id} className="p-3 bg-white border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <GripVertical className="w-4 h-4 text-gray-400 cursor-move" />
                              <span className="font-semibold text-sm text-[#0A2540]">{op.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-green-600">₹{op.calculated_cost || 0}</span>
                              <Button variant="ghost" size="sm" onClick={() => removeOperation(op.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>

                          {/* Cutting Operation */}
                          {op.id === 'cutting' && (
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-2">
                              <div>
                                <Label className="text-xs">Cutting Type</Label>
                                <Select value={op.cutting_type} onValueChange={(v) => updateOperation(idx, { cutting_type: v })}>
                                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {CUTTING_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="text-xs">
                                  Rate {op.cutting_type === 'Bandsaw' ? '(Rs/sq.in)' : '(Rs/mm/m)'}
                                </Label>
                                <Input 
                                  type="number" 
                                  step="0.01" 
                                  className="h-8" 
                                  value={op.rate || ''} 
                                  onChange={(e) => updateOperation(idx, { rate: e.target.value })}
                                  placeholder={op.cutting_type === 'Bandsaw' ? '1.5' : op.cutting_type === 'Laser' ? '8' : '15'}
                                />
                              </div>
                              {op.cutting_type === 'Bandsaw' && (
                                <>
                                  <div>
                                    <Label className="text-xs text-blue-600">Auto Area (sq.in)</Label>
                                    <Input 
                                      type="text" 
                                      className="h-8 bg-gray-50" 
                                      value={op.calculated_area || 0}
                                      disabled
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs">Override Area</Label>
                                    <Input 
                                      type="number" 
                                      step="0.001" 
                                      className="h-8" 
                                      value={op.area_override || ''} 
                                      onChange={(e) => updateOperation(idx, { area_override: e.target.value })}
                                      placeholder="Auto"
                                    />
                                  </div>
                                </>
                              )}
                              {(op.cutting_type === 'Laser' || op.cutting_type === 'Waterjet') && (
                                <div>
                                  <Label className="text-xs">Area (mm/m)</Label>
                                  <Input 
                                    type="number" 
                                    step="0.01" 
                                    className="h-8" 
                                    value={op.laser_area || ''} 
                                    onChange={(e) => updateOperation(idx, { laser_area: e.target.value })}
                                    placeholder="Enter area"
                                  />
                                </div>
                              )}
                              <div>
                                <Label className="text-xs">Cost Override</Label>
                                <Input 
                                  type="number" 
                                  step="0.01" 
                                  className="h-8" 
                                  value={op.manual_cost || ''} 
                                  onChange={(e) => updateOperation(idx, { manual_cost: e.target.value })}
                                  placeholder="Auto"
                                />
                              </div>
                            </div>
                          )}

                          {/* Forging Operation */}
                          {op.id === 'forging' && (
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-2">
                              <div>
                                <Label className="text-xs">Cost/kg (Rs)</Label>
                                <Input 
                                  type="number" 
                                  step="0.01" 
                                  className="h-8" 
                                  value={op.rate || ''} 
                                  onChange={(e) => updateOperation(idx, { rate: e.target.value })}
                                  placeholder="20"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-blue-600">Input Wt (kg)</Label>
                                <Input 
                                  type="text" 
                                  className="h-8 bg-blue-50 text-blue-700 font-medium" 
                                  value={op.input_weight || rawMaterial.gross_weight || 0}
                                  disabled
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-orange-600">Forging Wt (kg) *</Label>
                                <Input 
                                  type="number" 
                                  step="0.001" 
                                  className="h-8 border-orange-300" 
                                  value={op.forging_weight || ''} 
                                  onChange={(e) => updateOperation(idx, { forging_weight: e.target.value })}
                                  placeholder="Enter weight"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Cost Override</Label>
                                <Input 
                                  type="number" 
                                  step="0.01" 
                                  className="h-8" 
                                  value={op.manual_cost || ''} 
                                  onChange={(e) => updateOperation(idx, { manual_cost: e.target.value })}
                                  placeholder="Auto"
                                />
                              </div>
                            </div>
                          )}

                          {/* Normalizing Operation */}
                          {op.id === 'normalizing' && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                              <div>
                                <Label className="text-xs">Rate/kg (Rs)</Label>
                                <Input 
                                  type="number" 
                                  step="0.01" 
                                  className="h-8" 
                                  value={op.rate || ''} 
                                  onChange={(e) => updateOperation(idx, { rate: e.target.value })}
                                  placeholder="10"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-blue-600">Weight = Forging Wt</Label>
                                <Input 
                                  type="text" 
                                  className="h-8 bg-blue-50 text-blue-700 font-medium" 
                                  value={selectedOperations.find(o => o.id === 'forging')?.forging_weight || 0}
                                  disabled
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Weight Override</Label>
                                <Input 
                                  type="number" 
                                  step="0.001" 
                                  className="h-8" 
                                  value={op.weight_override || ''} 
                                  onChange={(e) => updateOperation(idx, { weight_override: e.target.value })}
                                  placeholder="Auto"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Cost Override</Label>
                                <Input 
                                  type="number" 
                                  step="0.01" 
                                  className="h-8" 
                                  value={op.manual_cost || ''} 
                                  onChange={(e) => updateOperation(idx, { manual_cost: e.target.value })}
                                  placeholder="Auto"
                                />
                              </div>
                            </div>
                          )}

                          {/* Hardening & Tempering Operation */}
                          {op.id === 'hardening' && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                              <div>
                                <Label className="text-xs">Rate/kg (Rs)</Label>
                                <Input 
                                  type="number" 
                                  step="0.01" 
                                  className="h-8" 
                                  value={op.rate || ''} 
                                  onChange={(e) => updateOperation(idx, { rate: e.target.value })}
                                  placeholder="20"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-blue-600">Default = Forging Wt</Label>
                                <Input 
                                  type="text" 
                                  className="h-8 bg-blue-50 text-blue-700 font-medium" 
                                  value={selectedOperations.find(o => o.id === 'forging')?.forging_weight || 0}
                                  disabled
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Weight (editable)</Label>
                                <Input 
                                  type="number" 
                                  step="0.001" 
                                  className="h-8" 
                                  value={op.weight_override || ''} 
                                  onChange={(e) => updateOperation(idx, { weight_override: e.target.value })}
                                  placeholder={selectedOperations.find(o => o.id === 'forging')?.forging_weight || 'Auto'}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Cost Override</Label>
                                <Input 
                                  type="number" 
                                  step="0.01" 
                                  className="h-8" 
                                  value={op.manual_cost || ''} 
                                  onChange={(e) => updateOperation(idx, { manual_cost: e.target.value })}
                                  placeholder="Auto"
                                />
                              </div>
                            </div>
                          )}

                          {/* Standard Operations (other than cutting, forging, normalizing, hardening) */}
                          {!['cutting', 'forging', 'normalizing', 'hardening'].includes(op.id) && (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                              <div>
                                <Label className="text-xs">Rate ({op.unit})</Label>
                                <Input 
                                  type="number" 
                                  step="0.01" 
                                  className="h-8" 
                                  value={op.rate || ''} 
                                  onChange={(e) => updateOperation(idx, { rate: e.target.value })}
                                  placeholder={op.defaultRate?.toString()}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Cost Override</Label>
                                <Input 
                                  type="number" 
                                  step="0.01" 
                                  className="h-8" 
                                  value={op.manual_cost || ''} 
                                  onChange={(e) => updateOperation(idx, { manual_cost: e.target.value })}
                                  placeholder="Auto"
                                />
                              </div>
                            </div>
                          )}

                          {/* Formula display */}
                          <div className="text-xs text-gray-400 mt-2 pt-2 border-t">
                            {op.id === 'cutting' && op.cutting_type === 'Bandsaw' && (
                              <span>Formula: Rate × Area | Area: {rawMaterial.rm_shape === 'Plate/Sheet' ? '0.0155 × W × B' : '0.0013 × OD²'}</span>
                            )}
                            {op.id === 'cutting' && op.cutting_type === 'Laser' && (
                              <span>Formula: Rate (₹8/mm/m) × Area mm/m</span>
                            )}
                            {op.id === 'cutting' && op.cutting_type === 'Waterjet' && (
                              <span>Formula: Rate (₹15/mm/m) × Area mm/m</span>
                            )}
                            {op.id === 'forging' && (
                              <span>Formula: Cost/kg × Forging Weight</span>
                            )}
                            {op.id === 'normalizing' && (
                              <span>Formula: Rate/kg × Forging Weight</span>
                            )}
                            {op.id === 'hardening' && (
                              <span>Formula: Rate/kg × Weight (default = Forging Weight)</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-4 text-right">
                    <span className="text-sm text-gray-500">Total Operation Cost: </span>
                    <span className="text-xl font-bold text-purple-600">₹{summary.total_operation_cost}</span>
                  </div>
                </div>
              )}
            </Card>

            {/* Machining Section */}
            <Card className="border-gray-200">
              <div 
                className="p-4 bg-orange-50 border-b cursor-pointer flex justify-between items-center"
                onClick={() => toggleSection('machining')}
              >
                <h2 className="font-semibold text-[#0A2540]">Machining Costing</h2>
                {expandedSections.machining ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </div>
              {expandedSections.machining && (
                <div className="p-4">
                  <Button variant="outline" size="sm" onClick={addMachiningStep} className="mb-4">
                    <Plus className="w-4 h-4 mr-2" /> Add Machining Step
                  </Button>

                  {machiningSteps.map((step, idx) => (
                    <div key={step.id} className="p-4 mb-3 bg-white border rounded-lg">
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                        <div>
                          <Label className="text-xs">Machine Type</Label>
                          <Select value={step.machine_type} onValueChange={(v) => updateMachiningStep(idx, { machine_type: v })}>
                            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {MACHINE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Size</Label>
                          <Select value={step.machine_size} onValueChange={(v) => updateMachiningStep(idx, { machine_size: v })}>
                            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {MACHINE_SIZES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Cycle Time (sec)</Label>
                          <Input type="number" className="h-8" value={step.cycle_time} onChange={(e) => updateMachiningStep(idx, { cycle_time: e.target.value })} />
                        </div>
                        <div>
                          <Label className="text-xs">Efficiency %</Label>
                          <Input type="number" className="h-8" value={step.efficiency} onChange={(e) => updateMachiningStep(idx, { efficiency: e.target.value })} />
                        </div>
                        <div>
                          <Label className="text-xs">Override Cost</Label>
                          <Input type="number" step="0.01" className="h-8" value={step.manual_cost || ''} onChange={(e) => updateMachiningStep(idx, { manual_cost: e.target.value })} placeholder="Auto" />
                        </div>
                        <div className="flex items-end justify-between">
                          <div>
                            <p className="text-xs text-gray-500">Cost/Piece</p>
                            <p className="font-bold text-orange-600">₹{step.calculated_cost || 0}</p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => removeMachiningStep(step.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        Rate: ₹{step.hourly_rate || 0}/hr | OPH: {step.oph || 0} | Formula: OPH = (3600/CycleTime) × Eff% | Cost = Rate ÷ OPH
                      </p>
                    </div>
                  ))}

                  <div className="mt-4 text-right">
                    <span className="text-sm text-gray-500">Total Machining Cost: </span>
                    <span className="text-xl font-bold text-orange-600">₹{summary.total_machining_cost}</span>
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1">
            <Card className="border-gray-200 sticky top-4">
              <div className="p-4 bg-[#0A2540] text-white rounded-t-lg">
                <h2 className="font-bold text-lg flex items-center gap-2">
                  <Calculator className="w-5 h-5" />
                  Cost Summary
                </h2>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Gross Weight</span>
                  <span className="font-semibold">{rawMaterial.gross_weight} kg</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Scrap Weight</span>
                  <span className="font-semibold">{rawMaterial.scrap_weight} kg</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Gross RM Value</span>
                  <span className="font-semibold">₹{rawMaterial.gross_rm_value}</span>
                </div>
                <div className="flex justify-between py-2 border-b text-orange-600">
                  <span>Scrap Recovery</span>
                  <span className="font-semibold">-₹{rawMaterial.scrap_value}</span>
                </div>
                <div className="flex justify-between py-2 border-b text-green-600">
                  <span>Net RM Cost</span>
                  <span className="font-bold">₹{summary.net_rm_cost}</span>
                </div>
                <div className="flex justify-between py-2 border-b text-purple-600">
                  <span>Operation Cost</span>
                  <span className="font-bold">₹{summary.total_operation_cost}</span>
                </div>
                <div className="flex justify-between py-2 border-b text-orange-600">
                  <span>Machining Cost</span>
                  <span className="font-bold">₹{summary.total_machining_cost}</span>
                </div>
                <div className="flex justify-between py-3 border-b bg-gray-50 -mx-4 px-4">
                  <span className="font-semibold">Manufacturing Cost</span>
                  <span className="font-bold text-lg">₹{summary.total_manufacturing_cost}</span>
                </div>
                
                <div className="pt-2">
                  <Label>Profit Margin %</Label>
                  <Input 
                    type="number" 
                    step="0.1" 
                    value={summary.profit_margin_percent} 
                    onChange={(e) => setSummary({...summary, profit_margin_percent: e.target.value})}
                    className="mt-1"
                  />
                </div>
                
                <div className="flex justify-between py-2">
                  <span className="text-gray-600">Profit Amount</span>
                  <span className="font-semibold text-blue-600">+₹{summary.profit_amount}</span>
                </div>

                <div className="flex justify-between py-4 bg-green-100 -mx-4 px-4 rounded-b-lg">
                  <span className="font-bold text-lg">Final Quotation</span>
                  <span className="font-bold text-2xl text-green-700">₹{summary.final_quotation_value}</span>
                </div>

                {header.quantity > 1 && (
                  <div className="text-center text-sm text-gray-500 pt-2">
                    Total for {header.quantity} pcs: <span className="font-bold">₹{(summary.final_quotation_value * header.quantity).toLocaleString('en-IN')}</span>
                  </div>
                )}
              </div>

              <div className="p-4 border-t flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => window.print()}>
                  <FileDown className="w-4 h-4 mr-2" /> Export
                </Button>
                <Button className="flex-1 bg-[#0A2540]" onClick={saveQuotation} disabled={saving}>
                  <Save className="w-4 h-4 mr-2" /> Save
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Masters Modal */}
      <Dialog open={showMastersModal} onOpenChange={setShowMastersModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Masters Configuration
            </DialogTitle>
            <DialogDescription>
              Manage default rates for raw materials, operations, and machines
            </DialogDescription>
          </DialogHeader>

          <Tabs value={mastersTab} onValueChange={setMastersTab} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="materials">Raw Materials</TabsTrigger>
              <TabsTrigger value="operations">Operations</TabsTrigger>
              <TabsTrigger value="machines">Machines</TabsTrigger>
            </TabsList>

            {/* Raw Materials Tab */}
            <TabsContent value="materials" className="flex-1 overflow-auto mt-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-500">Configure base rates for different materials</p>
                  <Button size="sm" variant="outline" onClick={addMasterMaterial}>
                    <Plus className="w-4 h-4 mr-1" /> Add Material
                  </Button>
                </div>
                <div className="space-y-2 max-h-[400px] overflow-auto">
                  {mastersMaterials.map((mat, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg border">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <Label className="text-xs">Material Type</Label>
                          <Input 
                            value={mat.material_type} 
                            onChange={(e) => {
                              const updated = [...mastersMaterials];
                              updated[idx] = {...updated[idx], material_type: e.target.value};
                              setMastersMaterials(updated);
                            }}
                            className="h-8"
                          />
                        </div>
                        {mat.material_type === 'Aluminium' && (
                          <div>
                            <Label className="text-xs text-blue-600">NALCO Rate</Label>
                            <Input 
                              type="number"
                              value={mat.nalco_rate || ''} 
                              onChange={(e) => {
                                const updated = [...mastersMaterials];
                                updated[idx] = {...updated[idx], nalco_rate: parseFloat(e.target.value) || 0};
                                setMastersMaterials(updated);
                              }}
                              className="h-8"
                            />
                          </div>
                        )}
                        {mat.material_type === 'Copper' && (
                          <div>
                            <Label className="text-xs text-orange-600">BME Rate</Label>
                            <Input 
                              type="number"
                              value={mat.bme_rate || ''} 
                              onChange={(e) => {
                                const updated = [...mastersMaterials];
                                updated[idx] = {...updated[idx], bme_rate: parseFloat(e.target.value) || 0};
                                setMastersMaterials(updated);
                              }}
                              className="h-8"
                            />
                          </div>
                        )}
                        {!['Aluminium', 'Copper'].includes(mat.material_type) && (
                          <div>
                            <Label className="text-xs">Manual Rate</Label>
                            <Input 
                              type="number"
                              value={mat.manual_rate || ''} 
                              onChange={(e) => {
                                const updated = [...mastersMaterials];
                                updated[idx] = {...updated[idx], manual_rate: parseFloat(e.target.value) || 0};
                                setMastersMaterials(updated);
                              }}
                              className="h-8"
                            />
                          </div>
                        )}
                        <div>
                          <Label className="text-xs">Conversion</Label>
                          <Input 
                            type="number"
                            value={mat.conversion || ''} 
                            onChange={(e) => {
                              const updated = [...mastersMaterials];
                              updated[idx] = {...updated[idx], conversion: parseFloat(e.target.value) || 0};
                              setMastersMaterials(updated);
                            }}
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Packaging</Label>
                          <Input 
                            type="number"
                            value={mat.packaging || ''} 
                            onChange={(e) => {
                              const updated = [...mastersMaterials];
                              updated[idx] = {...updated[idx], packaging: parseFloat(e.target.value) || 0};
                              setMastersMaterials(updated);
                            }}
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Transport</Label>
                          <Input 
                            type="number"
                            value={mat.transport || ''} 
                            onChange={(e) => {
                              const updated = [...mastersMaterials];
                              updated[idx] = {...updated[idx], transport: parseFloat(e.target.value) || 0};
                              setMastersMaterials(updated);
                            }}
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Surcharge</Label>
                          <Input 
                            type="number"
                            value={mat.surcharge || ''} 
                            onChange={(e) => {
                              const updated = [...mastersMaterials];
                              updated[idx] = {...updated[idx], surcharge: parseFloat(e.target.value) || 0};
                              setMastersMaterials(updated);
                            }}
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Interest %</Label>
                          <Input 
                            type="number"
                            value={mat.interest_percent || ''} 
                            onChange={(e) => {
                              const updated = [...mastersMaterials];
                              updated[idx] = {...updated[idx], interest_percent: parseFloat(e.target.value) || 0};
                              setMastersMaterials(updated);
                            }}
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Scrap %</Label>
                          <Input 
                            type="number"
                            value={mat.scrap_percent || ''} 
                            onChange={(e) => {
                              const updated = [...mastersMaterials];
                              updated[idx] = {...updated[idx], scrap_percent: parseFloat(e.target.value) || 0};
                              setMastersMaterials(updated);
                            }}
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Density (g/cm³)</Label>
                          <Input 
                            type="number"
                            step="0.01"
                            value={mat.density || ''} 
                            onChange={(e) => {
                              const updated = [...mastersMaterials];
                              updated[idx] = {...updated[idx], density: parseFloat(e.target.value) || 0};
                              setMastersMaterials(updated);
                            }}
                            className="h-8"
                          />
                        </div>
                        <div className="flex items-end">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setMastersMaterials(mastersMaterials.filter((_, i) => i !== idx))}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Operations Tab */}
            <TabsContent value="operations" className="flex-1 overflow-auto mt-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-500">Configure default rates for operations</p>
                  <Button size="sm" variant="outline" onClick={addMasterOperation}>
                    <Plus className="w-4 h-4 mr-1" /> Add Operation
                  </Button>
                </div>
                <div className="space-y-2 max-h-[400px] overflow-auto">
                  {mastersOperations.map((op, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg border flex items-center gap-4">
                      <div className="flex-1">
                        <Label className="text-xs">Operation Name</Label>
                        <Input 
                          value={op.operation_name} 
                          onChange={(e) => {
                            const updated = [...mastersOperations];
                            updated[idx] = {...updated[idx], operation_name: e.target.value};
                            setMastersOperations(updated);
                          }}
                          className="h-8"
                        />
                      </div>
                      <div className="w-28">
                        <Label className="text-xs">Default Rate</Label>
                        <Input 
                          type="number"
                          value={op.default_rate || ''} 
                          onChange={(e) => {
                            const updated = [...mastersOperations];
                            updated[idx] = {...updated[idx], default_rate: parseFloat(e.target.value) || 0};
                            setMastersOperations(updated);
                          }}
                          className="h-8"
                        />
                      </div>
                      <div className="w-28">
                        <Label className="text-xs">Unit</Label>
                        <Select 
                          value={op.unit} 
                          onValueChange={(v) => {
                            const updated = [...mastersOperations];
                            updated[idx] = {...updated[idx], unit: v};
                            setMastersOperations(updated);
                          }}
                        >
                          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Rs/kg">Rs/kg</SelectItem>
                            <SelectItem value="Rs/piece">Rs/piece</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setMastersOperations(mastersOperations.filter((_, i) => i !== idx))}
                        className="text-red-500 hover:text-red-700 mt-5"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Machines Tab */}
            <TabsContent value="machines" className="flex-1 overflow-auto mt-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-500">Configure hourly rates for machines</p>
                  <Button size="sm" variant="outline" onClick={addMasterMachine}>
                    <Plus className="w-4 h-4 mr-1" /> Add Machine
                  </Button>
                </div>
                <div className="space-y-2 max-h-[400px] overflow-auto">
                  {mastersMachines.map((machine, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg border flex items-center gap-4">
                      <div className="flex-1">
                        <Label className="text-xs">Machine Type</Label>
                        <Input 
                          value={machine.machine_type} 
                          onChange={(e) => {
                            const updated = [...mastersMachines];
                            updated[idx] = {...updated[idx], machine_type: e.target.value};
                            setMastersMachines(updated);
                          }}
                          className="h-8"
                        />
                      </div>
                      <div className="w-28">
                        <Label className="text-xs">Size</Label>
                        <Select 
                          value={machine.size} 
                          onValueChange={(v) => {
                            const updated = [...mastersMachines];
                            updated[idx] = {...updated[idx], size: v};
                            setMastersMachines(updated);
                          }}
                        >
                          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Small">Small</SelectItem>
                            <SelectItem value="Medium">Medium</SelectItem>
                            <SelectItem value="Large">Large</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-32">
                        <Label className="text-xs">Hourly Rate (₹)</Label>
                        <Input 
                          type="number"
                          value={machine.hourly_rate || ''} 
                          onChange={(e) => {
                            const updated = [...mastersMachines];
                            updated[idx] = {...updated[idx], hourly_rate: parseFloat(e.target.value) || 0};
                            setMastersMachines(updated);
                          }}
                          className="h-8"
                        />
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setMastersMachines(mastersMachines.filter((_, i) => i !== idx))}
                        className="text-red-500 hover:text-red-700 mt-5"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowMastersModal(false)}>
              Cancel
            </Button>
            <Button onClick={saveMasters} disabled={savingMasters} className="bg-[#0A2540]">
              {savingMasters ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Masters
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
